import { Component } from "react";
import { RouteComponentProps } from "react-router-dom";

import { County, GeographyService } from "@app/geography";
import { Project, ProjectService } from "@app/projects";
import { NearbyCountiesPanelContent, RegionGranularity, RegionSet } from "@app/region-sets";
import LookupRegionSet from "@app/region-sets/editor/lookupRegionSet";
import RegionSetService from "@app/region-sets/regionSetService";
import { BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import { CountyStatus } from "../create/interfaces";
import { ProjectRunConfigurationType, RegionSetStatusType } from "../enumerators";
import { QESButton } from "@app/shared/qes-standards";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ServiceAreaEditor } from "@app/region-sets/new-editor/ServiceAreaEditor";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags";
import { DownloadServiceAreaButton } from "@app/region-sets/new-editor/DownloadServiceAreaButton";
import ServiceAreaContextProvider from "@app/region-sets/new-editor/ServiceAreaEditorContext";
import { ResourceType } from "@app/resources";

interface EditRegionSetState {
  countyStatuses: CountyStatus[];
  isNextDisabled: boolean;
  project: Project | null;
  regionSet: RegionSet | null;
  submitClicked: boolean;
  isZipProject: boolean;

  triggerComplete?: () => void;
  updateCounties: County[];
}

type EditRegionSetProps = RouteComponentProps<{ projectId: string }> & FeatureFlagContextProps;

class WrappedEditRegionSet extends Component<EditRegionSetProps, EditRegionSetState> {
  private readonly projectService = new ProjectService();
  private readonly regionSetService = new RegionSetService();
  private readonly geographyService = new GeographyService();

  constructor(props: EditRegionSetProps) {
    super(props);

    this.state = {
      countyStatuses: [],
      isNextDisabled: false,
      isZipProject: false,
      project: null,
      regionSet: null,
      submitClicked: false,
      updateCounties: [],
    };
  }

  public async componentDidMount() {
    const projectId = Number(this.props.match.params.projectId);
    if (projectId) {
      const project = await this.projectService.getById(projectId);
      const regionSet = await this.regionSetService.getById(project.serviceAreaRegionSetId);

      const isZipProject = project.resource.resourceType === ResourceType.ZipAnalysisProject;
      const countyStatuses = await this.getCountyStatuses(project, isZipProject);

      this.setState({
        countyStatuses,
        isZipProject,
        project,
        regionSet,
      });
    }
  }

  public render() {
    const projectName = this.state.project ? this.state.project.resource.name : "Loading...";

    return (
      <Page documentTitle="Edit Service Area">
        <Breadcrumbs
          template={BreadcrumbTemplateEnum.NetworkAssessment}
          items={[
            {
              link: !!this.state.project ? `/projects/${this.state.project.projectId}` : "",
              text: projectName,
            },
            "Edit Service Area"
          ]}
        />
        <PageInner>
          <PageHeader title={projectName} />
          {this.state.project &&
            (
              <PageSection>
                <ServiceAreaContextProvider
                  countyStatuses={this.state.countyStatuses}
                  isZipProject={this.state.project.resource.resourceType === ResourceType.ZipAnalysisProject}
                  projectName={projectName}
                  regionSet={this.state.regionSet}
                >
                  <ServiceAreaEditor
                    countyStatuses={this.state.countyStatuses}
                    handleCountyStatusUpdates={this.handleCountyStatusUpdates}
                    regionSet={this.state.regionSet}
                    onInit={this.handleInit}
                    onUpdate={this.handleRegionUpdate}
                    onComplete={this.handleComplete}
                    updateCounties={this.state.updateCounties}
                  />
                  <div className="pt-1 d-flex flex-row w-100 align-items-center">
                    <div className="col-3 pl-0">
                      <QESButton qesTestId="edit-region-set_cancel" qesType="secondary-danger" disabled={this.state.submitClicked} onClick={this.handleCancel}>Cancel</QESButton>
                    </div>
                    <div className="ml-3">
                      <DownloadServiceAreaButton />
                    </div>
                    <div className="ml-auto">
                      {((this.state.countyStatuses && this.state.countyStatuses.length) || null) &&
                        <FlyoutLink
                          disabled={this.state.submitClicked}
                          classNames="qes-button-secondary mr15 pr-2 pl-2"
                          content={{
                            body: (<NearbyCountiesPanelContent
                              countySSAs={this.state.countyStatuses.map((countyStatus) => countyStatus.countySSA)}
                              onSubmitNearbyCounties={this.handleSubmitNearbyCounties}
                            />),
                            title: "Add Nearby Counties",
                          }}
                          testId="add-nearby-counties-flyout-link"
                          size="20"
                          onClose={() => { this.setState({ updateCounties: [] }) }}
                        >
                          Add Nearby Counties
                        </FlyoutLink>
                      }
                      <QESButton qesTestId="edit-region-set_submit" qesType="primary" disabled={this.isProjectRunning() || this.state.isNextDisabled || this.state.submitClicked} onClick={this.handleSubmit}>Submit</QESButton>
                    </div>
                  </div>
                </ServiceAreaContextProvider>
              </PageSection>
            )
          }
        </PageInner>
      </Page >
    );
  }

  private handleSubmit = () => {
    this.setState({ submitClicked: true }, () => { this.state.triggerComplete(); });
  }

  private handleRegionUpdate = (lookupRegionSet: LookupRegionSet) => {
    // For some reason an empty lookupRegionSet.children is not the same as {}, but serializing it works
    if (JSON.stringify(lookupRegionSet.children) !== "{}") {
      this.setState({
        isNextDisabled: false,
      });
    } else {
      this.setState({
        isNextDisabled: true,
      });
    }
  }

  private handleInit = (triggerComplete: () => void) => {
    this.setState({ triggerComplete });
  }

  private handleSubmitNearbyCounties = (updateCounties: County[], regionStatus: RegionSetStatusType) => {
    const statuses = updateCounties.map((county) =>
    ({
      countySSA: county.countySSA,
      countyZips: [],
      status: regionStatus,
    } as CountyStatus),
    );

    this.setState({
      countyStatuses: this.getUpdatedCountyStatuses(statuses),
      updateCounties,
    });
  }

  private handleComplete = async (serviceAreaRegionSet: RegionSet) => {
    await this.projectService.updateServiceArea(this.state.project.projectId, serviceAreaRegionSet.regionSetId);
    await this.getCountyRegionSetIds();
    this.props.history.goBack();
  }

  private handleCancel = () => {
    this.props.history.goBack();
  }

  private isProjectRunning = (): boolean => this.state.project && this.state.project.isRunning;

  private getCountyStatuses = async (project: Project, isZipTemplate: boolean) => {
    const statuses = isZipTemplate ? [] : await this.projectService.getCountyStatusInfo(
      project.projectId,
      project.serviceAreaRegionSetId,
      await this.projectService.maxRunConfigurationId(project, ProjectRunConfigurationType.DualMembershipPrimary),
    );

    return statuses;
  }

  private getCountyRegionSetIds = async () => {
    const counties = this.state.countyStatuses;

    const countiesWithoutRegionSet: RegionSet[] = [];
    for (const c of counties) {
      if (!c.regionSetId) {
        const newRegion: RegionSet = {
          granularity: RegionGranularity.County,
          partialRegionSets: c.countyZips.map<RegionSet>((cz) => {
            return {
              granularity: RegionGranularity.CountyZip,
              partialRegionSets: [],
              regionCode: c.countySSA,
              regionSetId: null,
              subRegionCode: cz,
            };
          }),
          regionCode: c.countySSA,
          regionSetId: null,
          subRegionCode: null,
        };

        countiesWithoutRegionSet.push(newRegion);
      }
    }

    //create all the region sets at once
    const createdRegionSets = await this.geographyService.getOrCreateRegionSets(countiesWithoutRegionSet);

    //match by countySSA on returned region sets to get the region set id
    for (const c of createdRegionSets) {
      const matchingCounty = counties.find((county) => county.countySSA === c.regionCode);
      matchingCounty.regionSetId = c.regionSetId;
    }

    this.setState({
      countyStatuses: counties,
    }, async () => {
      await this.projectService.setStatuses(this.state.project.projectId, this.state.countyStatuses);
    });
  }

  private handleCountyStatusUpdates = async (statuses: CountyStatus[]) => {
    this.setState({
      countyStatuses: this.getUpdatedCountyStatuses(statuses),
    });
  }

  private getUpdatedCountyStatuses = (statuses: CountyStatus[]) => {
    const updatedStatus = this.state.countyStatuses;
    for (const status of statuses) {
      const indexToReplace = updatedStatus.findIndex((item) => item.countySSA === status.countySSA);
      if (indexToReplace >= 0) {
        if (status.status === RegionSetStatusType.Unknown) {
          updatedStatus.splice(indexToReplace, 1);
        } else {
          updatedStatus[indexToReplace] = status;
        }
      } else {
        if (status.status !== RegionSetStatusType.Unknown) {
          updatedStatus.push(status);
        }
      }
    }

    return updatedStatus;
  }

  private handleCountyStatusForCheckedState = async (counties: County[]) => {
    const countiesToUpdate: CountyStatus[] = [];
    counties.map((county) => {
      const currentCounty = this.state.countyStatuses.find((item) => item.countySSA === county.countySSA);
      if (!currentCounty || (currentCounty && currentCounty.status === RegionSetStatusType.Unknown)) {
        countiesToUpdate.push({
          countySSA: county.countySSA,
          countyZips: [],
          regionSetId: null,
          status: RegionSetStatusType.Active,
        });
      }

    });
    this.handleCountyStatusUpdates(countiesToUpdate);
  }
}

const EditRegionSet = withFeatureFlagContext(WrappedEditRegionSet);
export default EditRegionSet;